import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatAED } from "../lib/api";
import Money from "./Money";

/**
 * Watch of the Week — homepage hero band directly under the Header.
 * Fetches `/api/watch-of-week`; renders nothing when no lot is featured
 * so the page falls through to the existing hero without any layout jump.
 *
 * The admin marks/unmarks a lot from Admin → Auctions → Auctions (☆/★).
 * Only one lot can hold the flag at a time (enforced server-side).
 */
export default function WatchOfTheWeek() {
  const [lot, setLot] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api
      .get("/watch-of-week")
      .then(({ data }) => {
        if (alive) setLot(data || null);
      })
      .catch(() => {
        if (alive) setLot(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (loading || !lot) return null;

  const heroImage = lot.images?.[0] || null;
  const rawDesc = (lot.description || "").trim();
  // Trim to a clean sentence boundary up to ~200 chars so the hero copy stays tight.
  const shortDesc =
    rawDesc.length > 200
      ? rawDesc.slice(0, 200).replace(/[.!?][^.!?]*$/, ".").trim()
      : rawDesc;
  const isLive = lot.status === "live";
  const ctaLabel = isLive ? "Join the auction" : "View lot details";

  return (
    <section
      className="relative overflow-hidden border-b border-white/10"
      data-testid="watch-of-the-week-section"
      aria-labelledby="wow-heading"
    >
      {/* Backdrop: watch photo, blurred + darkened for luxury magazine feel */}
      {heroImage && (
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20 scale-110"
          style={{ backgroundImage: `url(${heroImage})`, filter: "blur(24px)" }}
          aria-hidden="true"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-midnight-deep/70 via-midnight-deep/90 to-midnight-deep" aria-hidden="true" />

      <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-12">
        <div className="grid md:grid-cols-[280px_1fr] gap-6 md:gap-8 items-center">
          {/* Foreground image — compact square */}
          <div className="relative aspect-square w-full max-w-[280px] rounded overflow-hidden bg-black/40">
            {heroImage ? (
              <img
                src={heroImage}
                alt={lot.title}
                className="w-full h-full object-cover"
                data-testid="wow-photo"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/30 text-xs uppercase tracking-widest">
                No image
              </div>
            )}
          </div>

          {/* Copy + CTA */}
          <div>
            <div className="overline text-gold mb-2 flex items-center gap-3" data-testid="wow-kicker">
              <span>★ Watch of the Week</span>
              {isLive && (
                <span className="inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 border border-emerald-400/40 bg-emerald-500/10 text-emerald-300 rounded">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Live now
                </span>
              )}
            </div>

            <div className="text-xs uppercase tracking-widest text-white/60 mb-1" data-testid="wow-brand">
              {lot.brand}
            </div>
            <h2
              id="wow-heading"
              className="font-serif text-2xl lg:text-3xl leading-tight tracking-tight mb-3"
              data-testid="wow-title"
            >
              {lot.title}
            </h2>

            {shortDesc && (
              <p className="text-white/70 text-sm leading-relaxed mb-5" data-testid="wow-description">
                {shortDesc}
              </p>
            )}

            <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1.5 mb-5">
              {lot.current_bid_aed > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50">Current bid</div>
                  <div className="font-sans tabular-nums text-lg gold-text" data-testid="wow-current-bid">
                    <Money aed={lot.current_bid_aed} />
                  </div>
                </div>
              )}
              {lot.reference_number && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50">Reference</div>
                  <div className="font-sans tabular-nums text-xs">{lot.reference_number}</div>
                </div>
              )}
              {lot.year_of_manufacture && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/50">Year</div>
                  <div className="font-sans tabular-nums text-xs">{lot.year_of_manufacture}</div>
                </div>
              )}
            </div>

            <Link
              to={`/auctions/${lot.lot_id}`}
              data-testid="wow-join-btn"
              className="inline-flex items-center bg-gold text-black px-6 py-2.5 uppercase tracking-widest text-xs font-medium hover:bg-gold-hover transition-all hover:-translate-y-0.5 hover:shadow-[0_0_24px_rgba(212,175,55,0.35)]"
            >
              {ctaLabel} →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
